import React, {
  useState,
  FormEventHandler,
  FormEvent,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Input, Divider, Button, message as Message } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import { useTranslation } from 'react-i18next';
import { GlobalModalHandler, GlobalModalClose } from '../layout/GlobalModal';
import { SettingsRouteProps } from './Settings';
import { sendContactForm } from '../../store/settings/actions';
import { getAppGlobalState } from '../../store/app-globals/selectors';

import LogoIcon from '../../assets/images/png/swap/swaparoundlogo.png';
import EmailSentIcon from '../../assets/images/png/email-sent.png';

export const ContactFormSent: React.FC = () => {
  return (
    <div className="modal-content-wrapper">
      <div className="modal-content-icon">
        <img src={EmailSentIcon} alt="modal-content-icon" />
      </div>
      <div className="modal-content-description">
        <span>Thank you for your message!</span>
      </div>
      <div className="button-container">
        <div className="modal-content-extra">
          We will answer you as soon as possible.
        </div>
        <Button
          block
          type="primary"
          shape="round"
          onClick={() => GlobalModalClose()}
          className="continue-button continue-button-primary"
        >
          <span className="continue-button-text">Continue</span>
        </Button>
      </div>
    </div>
  );
};

interface ContactFormState {
  email: string;
  message: string;
}

const initContactFormState: ContactFormState = {
  email: '',
  message: '',
};

export const ContactUs: React.FC<SettingsRouteProps> = ({ setHeaderTitle }) => {
  const { isLoading, message, hasError } = useSelector(getAppGlobalState);
  const [form, setForm] = useState(initContactFormState);
  const { t } = useTranslation();
  const [valid, setValid] = useState(false);
  const dispatch = useDispatch();
  const isFormSent = useRef(false);

  const handleFieldChange = (
    field: keyof ContactFormState
  ): FormEventHandler => (e: FormEvent<HTMLInputElement>): void => {
    setForm({ ...form, [field]: e.currentTarget.value });
  };

  const validate: React.EffectCallback = (): void => {
    setValid(form.email !== '' && form.message !== '');
  };

  const handleSendButton = () => {
    dispatch(sendContactForm(form));
    isFormSent.current = true;
  };

  const formSent = () => {
    if (isFormSent.current && !isLoading && !hasError) {
      setForm({ ...form, email: '', message: '' });
      GlobalModalHandler({ content: <ContactFormSent /> });
    }
    if (isFormSent.current && !isLoading && hasError) {
      Message.error('Something went wrong');
    }
  };

  const setHeader: React.EffectCallback = () => {
    if (setHeaderTitle) setHeaderTitle('Contact Us');
  };
  useEffect(validate, [form]);
  useEffect(setHeader, [setHeaderTitle]);
  useEffect(formSent, [isLoading]);
  return (
    <React.Fragment>
      <Col className="about-us-block margin-top">
        <img src={LogoIcon} alt="" style={{ height: 200, width: 200 }} />
        <div className="about-us-app">
          {t('Have any question or need help?')}
        </div>
        <div className="extra-text margin-top">
          {t('Feel free to write us. We will answer you as soon as possible.')}
        </div>
      </Col>
      <Col className="contact-us-block">
        <Col>
          <Input
            value={form.email}
            placeholder={t('YOUR_EMAIL')}
            onChange={handleFieldChange('email')}
          />
          <Divider className="contact-us-divider" />
          <TextArea
            value={form.message}
            placeholder={t('WRITE_MESSAGE')}
            rows={4}
            onChange={handleFieldChange('message')}
          />
        </Col>
        <Col className="primary-button-basic-container contact-us-button">
          <Button
            disabled={!valid}
            onClick={handleSendButton}
            shape="round"
            className="primary-button-basic"
            loading={isLoading}
          >
            <span className="continue-button-text">{message || t('Send')}</span>
          </Button>
        </Col>
      </Col>
    </React.Fragment>
  );
};
